import React, { useState } from 'react';
import { authService } from '../services';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Toast from '../components/ui/Toast';
import { Link } from 'react-router-dom';

const AuthTestPage: React.FC = () => {
  const [email, setEmail] = useState('newuser@example.com');
  const [username, setUsername] = useState('newuser');
  const [password, setPassword] = useState('password123');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [toast, setToast] = useState<{show: boolean, type: 'success' | 'error' | 'warning' | 'info', message: string}>({
    show: false,
    type: 'info',
    message: ''
  });

  const showToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
  };

  const testWithExistingUser = () => {
    setEmail('test@example.com');
    setUsername('testuser');
    setPassword('password123');
    setResult('Switched to existing user credentials. Try logging in instead of registering.');
    showToast('info', 'Switched to existing user credentials');
  };

  const testWithNewUser = () => {
    setEmail('newuser@example.com');
    setUsername('newuser');
    setPassword('password123');
    setResult('Switched to new user credentials. Try registering first.');
    showToast('info', 'Switched to new user credentials');
  };

  const testWithDemoUser = () => {
    setEmail('demo@example.com');
    setUsername('demo');
    setPassword('demo123');
    setResult('Switched to demo user credentials. This user exists and can be used for login testing.');
    showToast('info', 'Switched to demo user credentials');
  };

  const testRegister = async () => {
    setLoading(true);
    setResult('');
    
    try {
      const response = await authService.register({
        email,
        username,
        password
      });
      
      setResult(`Registration successful! User: ${response.user.username}, Token: ${response.token.substring(0, 20)}...`);
      showToast('success', 'Registration successful!');
    } catch (error: any) {
      console.error('Registration error:', error);
      console.error('Full error response:', error.response?.data);
      
      let errorMessage = 'Unknown error';
      let errorCode = 'UNKNOWN';
      
      // Handle transformed errors from auth service
      if (error.code && error.message) {
        errorCode = error.code;
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        errorCode = error.response.data.code || 'API_ERROR';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
        errorCode = error.response.data.error.code || 'API_ERROR';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const fullErrorInfo = `Registration failed (${error.response?.status || errorCode}): ${errorMessage}`;
      if (error.response?.data) {
        setResult(`${fullErrorInfo}\n\nFull response: ${JSON.stringify(error.response.data, null, 2)}`);
      } else if (error.details) {
        setResult(`${fullErrorInfo}\n\nDetails: ${JSON.stringify(error.details, null, 2)}`);
      } else {
        setResult(fullErrorInfo);
      }
      showToast('error', `Registration failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const testLogin = async () => {
    setLoading(true);
    setResult('');
    
    try {
      const response = await authService.login({
        email,
        password
      });
      
      setResult(`Login successful! User: ${response.user.username}, Token: ${response.token.substring(0, 20)}...`);
      showToast('success', 'Login successful!');
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Full error response:', error.response?.data);
      
      let errorMessage = 'Unknown error';
      let errorCode = 'UNKNOWN';
      
      // Handle transformed errors from auth service
      if (error.code && error.message) {
        errorCode = error.code;
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        errorCode = error.response.data.code || 'API_ERROR';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
        errorCode = error.response.data.error.code || 'API_ERROR';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const fullErrorInfo = `Login failed (${error.response?.status || errorCode}): ${errorMessage}`;
      if (error.response?.data) {
        setResult(`${fullErrorInfo}\n\nFull response: ${JSON.stringify(error.response.data, null, 2)}`);
      } else if (error.details) {
        setResult(`${fullErrorInfo}\n\nDetails: ${JSON.stringify(error.details, null, 2)}`);
      } else {
        setResult(fullErrorInfo);
      }
      showToast('error', `Login failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const testLogout = async () => {
    setLoading(true);
    setResult('');
    
    try {
      await authService.logout();
      setResult('Logout successful!');
      showToast('success', 'Logout successful!');
    } catch (error: any) {
      setResult(`Logout failed: ${error.message || error.code || 'Unknown error'}`);
      showToast('error', `Logout failed: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testHealthCheck = async () => {
    setLoading(true);
    setResult('');
    
    try {
      const isHealthy = await authService.healthCheck();
      setResult(`Health check: ${isHealthy ? 'Authenticated' : 'Not authenticated'}`);
      showToast(isHealthy ? 'success' : 'warning', `Health check: ${isHealthy ? 'Authenticated' : 'Not authenticated'}`);
    } catch (error: any) {
      setResult(`Health check failed: ${error.message || error.code || 'Unknown error'}`);
      showToast('error', `Health check failed: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testGetCurrentUser = async () => {
    setLoading(true);
    setResult('');
    
    try {
      const user = await authService.getCurrentUser();
      setResult(`Current user: ${user.username} (${user.email})`);
      showToast('success', `Current user: ${user.username}`);
    } catch (error: any) {
      setResult(`Get current user failed: ${error.message || error.code || 'Unknown error'}`);
      showToast('error', `Get current user failed: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testTokenRefresh = async () => {
    setLoading(true);
    setResult('');
    
    try {
      const isValid = await authService.ensureValidToken();
      setResult(`Token validation: ${isValid ? 'Valid' : 'Invalid/Expired'}`);
      showToast(isValid ? 'success' : 'warning', `Token validation: ${isValid ? 'Valid' : 'Invalid/Expired'}`);
    } catch (error: any) {
      setResult(`Token refresh failed: ${error.message || error.code || 'Unknown error'}`);
      showToast('error', `Token refresh failed: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testFormPersistenceBugFix = () => {
    setResult(`Form Persistence Bug Fix Test Instructions:

REGISTRATION FORM TEST:
1. Click "Test Registration Form Persistence" button below
2. This will open the registration page in a new tab
3. Fill out the form with these test credentials:
   - Email: test@example.com (existing user - will cause error)
   - Username: testuser
   - Password: password123
4. Click "Create Account" button
5. You should see an error message: "User with this email already exists"
6. ✅ BUG FIX VERIFICATION: The form fields should remain filled after the error
7. ❌ OLD BEHAVIOR: Form fields would be cleared (requiring user to re-enter)

LOGIN FORM TEST:
1. Click "Test Login Form Persistence" button below
2. This will open the login page in a new tab
3. Fill out the form with these test credentials:
   - Email: nonexistent@example.com (will cause error)
   - Password: wrongpassword
4. Click "Sign In" button
5. You should see an error message: "Invalid email or password"
6. ✅ BUG FIX VERIFICATION: The form fields should remain filled after the error
7. ❌ OLD BEHAVIOR: Form fields would be cleared (requiring user to re-enter)

The fix uses sessionStorage to persist form data across component re-mounts that occur during the authentication error handling process.`);
    showToast('info', 'Form persistence test instructions provided');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Authentication Service Test</h1>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Credentials</h2>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={testWithNewUser}
                variant="secondary"
                size="sm"
                className="flex-1"
              >
                Use New User
              </Button>
              <Button
                onClick={testWithExistingUser}
                variant="secondary"
                size="sm"
                className="flex-1"
              >
                Use Existing User
              </Button>
              <Button
                onClick={testWithDemoUser}
                variant="secondary"
                size="sm"
                className="flex-1"
              >
                Use Demo User
              </Button>
            </div>
            
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="newuser@example.com"
            />
            
            <Input
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="newuser"
            />
            
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password123"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication Tests</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={testRegister}
              disabled={loading}
              className="w-full"
              variant="secondary"
            >
              Test Register
            </Button>
            
            <Button
              onClick={testLogin}
              disabled={loading}
              className="w-full"
            >
              Test Login
            </Button>
            
            <Button
              onClick={testLogout}
              disabled={loading}
              className="w-full"
              variant="secondary"
            >
              Test Logout
            </Button>
            
            <Button
              onClick={testHealthCheck}
              disabled={loading}
              className="w-full"
              variant="secondary"
            >
              Health Check
            </Button>
            
            <Button
              onClick={testGetCurrentUser}
              disabled={loading}
              className="w-full"
              variant="secondary"
            >
              Get Current User
            </Button>
            
            <Button
              onClick={testTokenRefresh}
              disabled={loading}
              className="w-full"
              variant="secondary"
            >
              Token Refresh
            </Button>
          </div>
        </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-green-900 mb-4">🐛 Bug Fix Verification</h2>
            <p className="text-green-800 mb-4">
              <strong>Form Persistence Fix:</strong> This test verifies that form fields are preserved when authentication fails,
              preventing users from having to re-enter their information after an error. This applies to both registration and login forms.
            </p>

            <div className="flex gap-4">
              <Button
                onClick={testFormPersistenceBugFix}
                className="flex-1"
                variant="primary"
              >
                📋 Show Test Instructions
              </Button>

              <Link
                to="/auth/register"
                target="_blank"
                className="flex-1"
              >
                <Button
                  className="w-full"
                  variant="secondary"
                >
                  🔗 Open Registration Page
                </Button>
              </Link>

              <Link
                to="/auth/login"
                target="_blank"
                className="flex-1"
              >
                <Button
                  className="w-full"
                  variant="secondary"
                >
                  🔗 Open Login Page
                </Button>
              </Link>
            </div>
          </div>

        {result && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Result</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">{result}</pre>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication Status</h2>
          <div className="space-y-2">
            <p><strong>Is Authenticated:</strong> {authService.isAuthenticated() ? 'Yes' : 'No'}</p>
            <p><strong>Has Refresh Token:</strong> {authService.hasRefreshToken() ? 'Yes' : 'No'}</p>
            <p><strong>Current Token:</strong> {authService.getCurrentToken() ? `${authService.getCurrentToken()?.substring(0, 20)}...` : 'None'}</p>
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast
          id="auth-test-toast"
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          duration={5000}
        />
      )}
    </div>
  );
};

export default AuthTestPage;
